import type { ReactNode, RefAttributes } from 'react';
import { useContext, useRef } from 'react';
import type {
  PopoverProps as AriaPopoverProps,
  PopoverRenderProps,
} from 'react-aria-components';
import {
  OverlayTriggerStateContext,
  Popover as AriaPopover,
  PopoverContext,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { PortalEventBoundary } from '@/lib/base/overlays/portal-event-boundary';
import { useDismissOnInteractOutside } from '@/lib/base/overlays/useDismissOnInteractOutside';
import { cx } from '@/utils/cx';

interface PopoverProps
  extends Omit<AriaPopoverProps, 'children'>, RefAttributes<HTMLElement> {
  /**
   * Controls the panel's max height, not the trigger's dimensions. It accepts
   * the same five steps as the form-control size scale so a Select can forward
   * its own `size` straight through; everything from `md` up gets the taller
   * panel. Intentionally spelled out rather than importing `SelectSize` —
   * Popover is a generic overlay and shouldn't depend on the forms module.
   *
   * `available` applies NO cap of its own: the panel is bounded only by the
   * inline `maxHeight` react-aria computes from the space actually available
   * after flipping. Use it for panels with a pinned footer (Apply/Done) that
   * must never be pushed below the fold — the forced `!important` caps above
   * override that inline value (see popover-viewport-fit.test.ts).
   */
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'available';
  children?: ReactNode;
}

const TALL_PANEL_SIZES = new Set(['md', 'lg', 'xl']);

/** Stable no-op for the dismiss hook when no trigger state is in context. */
const NOOP_DISMISS = () => {};

/**
 * Non-modal popover with outside-click dismissal (useDismissOnInteractOutside).
 *
 * Under a `DialogTrigger`, put a react-aria `Dialog` INSIDE this popover.
 * DialogTrigger stamps `aria-controls` on the trigger with an id it hands to
 * that Dialog; a non-modal popover root does not take the id or role=dialog
 * itself. Without the Dialog the trigger's `aria-controls` dangles, so the
 * dismissal hook can't recognise its own trigger (re-clicking it reopens
 * instead of closing) and focus never enters the popover (Escape does
 * nothing). A modal popover used to hide that by acting as the dialog.
 * Select / ComboBox / MenuTrigger wire their own listbox or menu ids.
 */
export const Popover = ({ children, ...props }: PopoverProps) => {
  const zIndex = useStackingZIndex();
  // Non-modal popovers disable react-aria's built-in outside-pointer
  // dismissal; restore it here (full rationale in
  // useDismissOnInteractOutside).
  const popoverRef = useRef<HTMLElement>(null);
  const triggerState = useContext(OverlayTriggerStateContext);
  const popoverCtx = useContext(PopoverContext);
  // ComboBox is the one trigger that must NOT get the hook. Its input is both
  // the trigger (aria-controls points into the popover) and the text field, so
  // the hook's own-trigger path would swallow a caret-placement press and close
  // the list — and menuTrigger='focus' never reopens it on click. ComboBox does
  // not need the hook anyway: useComboBox closes on ANY input blur, including a
  // blur to <body> from a click on non-focusable page area.
  //
  // Select DOES need it. Its focus lives inside the listbox, and the only close
  // react-aria leaves a non-modal popover is `shouldCloseOnBlur`, which ignores
  // a blur whose relatedTarget is null — so a click on non-focusable content
  // (a side-nav heading, slideout background) left the listbox open. Its
  // trigger is a separate button, so the own-trigger swallow gives the correct
  // re-click-to-close toggle. Pinned by outside-click-dismissal.test.tsx.
  const isComboBoxTrigger =
    (popoverCtx as { trigger?: string } | null)?.trigger === 'ComboBox';
  useDismissOnInteractOutside(
    popoverRef,
    isComboBoxTrigger ? false : (triggerState?.isOpen ?? false),
    triggerState?.close ?? NOOP_DISMISS
  );
  return (
    // The boundary must wrap the AriaPopover ELEMENT (not its children):
    // react-aria's own Escape/keyboard handling lives on the popover root, and
    // React only skips ancestors of the boundary — see portal-event-boundary.
    <PortalEventBoundary>
      <AriaPopover
        placement='bottom'
        containerPadding={0}
        offset={4}
        // Non-modal by default — see DropdownPopover (dropdown.tsx) for the full
        // rationale: the modal underlay renders at z-index auto and paints
        // beneath stacked surfaces (slideouts), so outside-click dismissal never
        // fires there. Overridable via props (spread is after this default).
        isNonModal
        {...props}
        // After the spread on purpose: the dismiss hook's ref must win — a
        // caller-supplied ref would silently disable outside-click dismissal.
        // (No consumer passes one today; support merging if that changes.)
        ref={popoverRef}
        className={(state: PopoverRenderProps) =>
          cx(
            'min-w-(--trigger-width) w-max origin-(--trigger-anchor-point) overflow-x-hidden overflow-y-auto rounded-lg bg-primary py-1 shadow-lg ring-1 ring-secondary_alt outline-hidden will-change-transform',
            // `available`: no forced cap — react-aria's inline maxHeight rules.
            props.size !== 'available' && 'max-h-64!',

            state.isEntering &&
              'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
            state.isExiting &&
              'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5',
            TALL_PANEL_SIZES.has(props.size) && 'max-h-80!',

            typeof props.className === 'function'
              ? props.className(state as never)
              : props.className
          )
        }
        style={{ zIndex, ...props.style }}
      >
        <StackingLayer>{children}</StackingLayer>
      </AriaPopover>
    </PortalEventBoundary>
  );
};
